import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Check, Save } from "lucide-react";
import { Boton } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { seccion, type SeccionId } from "../secciones";
import { useDatos } from "../store/store";
import { useDatosApi } from "../DatosContext";
import { IndicadorGuardado } from "./IndicadorGuardado";

/** Botón "Guardar" explícito (el autoguardado sigue): Pablo lo pidió para no dudar si quedó. */
function BotonGuardar() {
  const api = useDatosApi();
  const toast = useToast();
  const pendiente = useDatos((s) => s.meta.revisionLocal > s.meta.revisionGuardada);
  const [guardando, setGuardando] = useState(false);
  if (api.modo === "demo") return null;
  async function guardar() {
    setGuardando(true);
    const ok = await api.guardarBorrador();
    setGuardando(false);
    if (ok) toast.ok("Guardado."); else toast.error("No pude guardar. Revisa tu conexión e intenta de nuevo.");
  }
  return <Boton tamano="sm" variante={pendiente ? "primario" : "secundario"} cargando={guardando} onClick={() => void guardar()}><Save className="h-4 w-4" aria-hidden /> Guardar</Boton>;
}

/** Marco de cada sección: cabecera fija con "← Mis datos", guardado y Guardar; pie que regresa al menú de pasos. */
export function SeccionLayout({ id, children, intro }: { id: SeccionId; children: ReactNode; intro?: ReactNode }) {
  const s = seccion(id)!;
  const [, navegar] = useLocation();
  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (!h) { window.scrollTo({ top: 0 }); return; }
    const t = setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(t);
  }, [id]);
  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-14 z-30 -mx-4 flex items-center justify-between gap-3 border-b border-borde bg-fondo/95 px-4 py-2 backdrop-blur">
        <Link href="/datos" className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-tinta-suave hover:text-tinta"><ArrowLeft className="h-4 w-4" aria-hidden /> Mis datos</Link>
        <div className="flex items-center gap-3"><IndicadorGuardado /><BotonGuardar /></div>
      </div>
      <div>
        {s.taller && <p className="eyebrow text-tinta-suave">{s.taller}</p>}
        <h1 className="text-2xl font-semibold">{s.titulo}</h1>
        {intro ? <div className="mt-1 text-tinta-suave">{intro}</div> : <p className="mt-1 text-tinta-suave">{s.queDa}</p>}
      </div>
      {children}
      <div className="mt-2 flex flex-col gap-2 border-t border-borde pt-4 sm:flex-row sm:justify-end">
        <Boton className="w-full sm:w-auto" onClick={() => navegar("/datos")}><Check className="h-4 w-4" aria-hidden /> Listo, volver a Mis datos</Boton>
      </div>
    </div>
  );
}

/** Bloque dentro de una sección, con ancla. */
export function Bloque({ id, titulo, ayuda, children, accion }: { id?: string; titulo: string; ayuda?: ReactNode; children: ReactNode; accion?: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 rounded-md border border-borde bg-white">
      <header className="flex items-start justify-between gap-3 border-b border-borde px-4 py-3">
        <div>
          <h2 className="text-base font-semibold">{titulo}</h2>
          {ayuda && <div className="mt-0.5 text-sm text-tinta-suave">{ayuda}</div>}
        </div>
        {accion}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
